import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { openCheckout } from "@/lib/payments";
import { fmtINR, INTENT_LABEL } from "@/lib/membership";

export default function PlanSelection({ member, onActivated }) {
  const queryClient = useQueryClient();
  const [payingId, setPayingId] = useState(null);
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["membership-plans"],
    queryFn: async () => (await api.get("/membership-plans")).data,
  });
  const { data: dawnDiscount } = useQuery({
    queryKey: ["dawn-discount-eligibility"],
    queryFn: async () => (await api.get("/member/dawn/discount-eligibility")).data,
    staleTime: 60_000,
  });
  const dawnAmount = Number(dawnDiscount?.membership_discount_amount || 0);

  const pay = async (plan) => {
    setPayingId(plan.id);
    try {
      const { data: order } = await api.post("/membership/checkout", { plan_id: plan.id });
      let resp;
      try {
        resp = await openCheckout({
          keyId: order.key_id, orderId: order.order_id, amount: order.amount, currency: order.currency,
          description: order.plan_name, prefill: { name: member.name, email: member.email },
          tracking: { content_name: order.plan_name, content_type: "membership", product_type: "membership" },
        });
      } catch (dismissErr) {
        toast.info("Payment window closed. You can try again anytime.");
        return;
      }
      const { data } = await api.post("/membership/verify", {
        membership_id: order.membership_id,
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        fbp: resp.fbp, fbc: resp.fbc,
      });
      toast.success("Payment verified. Membership activated.");
      queryClient.invalidateQueries({ queryKey: ["dawn-discount-eligibility"] });
      onActivated(data.membership);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not complete payment.");
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div data-testid="membership-plans" className="space-y-6">
      <div>
        <p className="mt-2 text-sm text-ptf-text/60 max-w-2xl">
          Membership gives you access to apply for Planet Trotter's Training and Volunteer opportunities.
        </p>
      </div>

      {dawnAmount > 0 && (
        <p data-testid="dawn-membership-discount-banner" className="border border-ptf-secondary/30 bg-ptf-secondary/[0.05] px-5 py-4 text-sm text-ptf-secondary leading-relaxed">
          As a paid DAWN applicant, you get ₹{dawnAmount} off your membership fee below, applied automatically at checkout.
        </p>
      )}

      {isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {plans.map((plan) => {
            const base = Math.max(plan.base_amount - dawnAmount, 0);
            const gst = Math.round(base * plan.gst_rate) / 100;
            const total = Math.round((base + gst) * 100) / 100;
            return (
            <div key={plan.id} data-testid={`plan-card-${plan.code.toLowerCase()}`} className="bg-white border border-ptf-primary/12 p-6 flex flex-col">
              <p className="text-[0.6rem] uppercase tracking-[0.16em] text-ptf-secondary">{INTENT_LABEL[plan.intent]} · {plan.duration_label}</p>
              <h3 className="mt-1 font-display uppercase text-xl tracking-tight text-ptf-primary">{plan.name}</h3>
              {plan.description && <p className="mt-2 text-sm text-ptf-text/65 leading-relaxed flex-1">{plan.description}</p>}
              <div className="mt-5 border-t border-ptf-primary/10 pt-4 space-y-1.5 text-sm">
                <div className="flex justify-between text-ptf-text/70"><span>Membership Fee</span><span data-testid="plan-base">{fmtINR(base)}</span></div>
                {dawnAmount > 0 && <div className="flex justify-between text-ptf-secondary text-xs"><span>DAWN Applicant Discount</span><span>&minus; {fmtINR(dawnAmount)}</span></div>}
                <div className="flex justify-between text-ptf-text/70"><span>GST @ {plan.gst_rate.toFixed(0)}%</span><span data-testid="plan-gst">{fmtINR(gst)}</span></div>
                <div className="flex justify-between font-semibold text-ptf-primary pt-1.5 border-t border-ptf-primary/10"><span>Total Payable</span><span data-testid="plan-total">{fmtINR(total)}</span></div>
              </div>
              <button
                type="button" data-testid={`plan-pay-${plan.code.toLowerCase()}`} onClick={() => pay(plan)} disabled={payingId === plan.id}
                className="mt-5 inline-flex items-center justify-center gap-2 bg-ptf-primary text-ptf-bg px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] hover:bg-ptf-secondary transition-colors disabled:opacity-60"
              >
                {payingId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {payingId === plan.id ? "Processing" : "Pay & Join"}
              </button>
            </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-ptf-text/45">Payments are processed securely via Razorpay. Membership activates only after payment is verified.</p>
    </div>
  );
}
